const normalizeDateString = (value) => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
};

const parseNumber = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const normalizeFlag = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true') return true;
    if (normalized === 'false') return false;
  }
  return null;
};

const normalizeState = (value) => {
  if (typeof value !== 'string') return null;
  const normalized = value.trim().toLowerCase().replace(/\s+/g, '_');
  return normalized || null;
};

export const getTrialAccessPayload = (entity) => {
  if (!entity || typeof entity !== 'object') return null;
  if (entity.trial_access && typeof entity.trial_access === 'object') return entity.trial_access;
  if (entity.organization?.trial_access && typeof entity.organization.trial_access === 'object') {
    return entity.organization.trial_access;
  }
  return null;
};

export const normalizeTrialAccess = (trialAccess) => {
  if (!trialAccess || typeof trialAccess !== 'object') {
    return {
      raw: null,
      state: null,
      isTrial: false,
      isPermanent: false,
      accessBlocked: false,
      daysRemaining: null,
      expiresAt: null,
      startedAt: null,
      hasKnownState: false,
    };
  }

  const state = normalizeState(
    trialAccess.state ??
      trialAccess.status ??
      trialAccess.trial_status ??
      trialAccess.access_state
  );
  const isTrial = normalizeFlag(trialAccess.is_trial);
  const isPermanent = normalizeFlag(trialAccess.is_permanent);
  const accessBlocked =
    normalizeFlag(
      trialAccess.access_blocked ??
        trialAccess.access_locked ??
        trialAccess.locked ??
        trialAccess.blocked
    );
  const daysRemaining = parseNumber(trialAccess.days_remaining ?? trialAccess.days_left);
  const expiresAt = normalizeDateString(trialAccess.expires_at ?? trialAccess.expires_on);
  const startedAt = normalizeDateString(trialAccess.started_at ?? trialAccess.started_on);

  const blockedByState = ['expired', 'blocked', 'locked', 'inactive'].includes(state);
  const trialByState = ['trial', 'active_trial', 'trial_active', 'active'].includes(state);
  const permanentByState = ['permanent', 'active_permanent'].includes(state);

  return {
    raw: trialAccess,
    state,
    isTrial: isTrial === true || (isTrial == null && trialByState),
    isPermanent: isPermanent === true || (isPermanent == null && permanentByState),
    accessBlocked: accessBlocked === true || (accessBlocked == null && blockedByState),
    daysRemaining,
    expiresAt,
    startedAt,
    hasKnownState:
      state != null ||
      isTrial != null ||
      isPermanent != null ||
      accessBlocked != null ||
      daysRemaining != null ||
      expiresAt != null ||
      startedAt != null,
  };
};

export const getTrialAccessSummary = (entity) =>
  normalizeTrialAccess(getTrialAccessPayload(entity));

export const getTrialStatusDisplay = (entity) => {
  const trialAccess = getTrialAccessSummary(entity);

  let status = 'none';

  if (trialAccess.accessBlocked || trialAccess.state === 'expired') {
    status = 'expired';
  } else if (trialAccess.state === 'pending') {
    status = 'pending';
  } else if (trialAccess.isTrial) {
    status = 'active';
  }

  const colorScheme =
    status === 'expired'
      ? 'red'
      : status === 'active'
        ? 'green'
        : status === 'pending'
          ? 'yellow'
          : 'gray';

  return {
    ...trialAccess,
    trialStatus: status,
    colorScheme,
  };
};
